import S, { DataSignal } from 's-js';
import TZDate from 'lib/TZDate';
import { IAjax } from 'lib/Ajax';
import { DataCacheCollection } from 'lib/DataCache';

import { PCCache } from 'data/PCCache';

import { ApprovalOfCharges, ApprovalOfChargesOptions } from 'data/models/ApprovalOfCharges';
import { DraftChargeCollection, DraftChargeCollectionOptions } from 'data/models/DraftChargeCollection';
import { BillCoverage } from 'data/models/BillCoverage';
import { BillingSystem } from 'data/models/BillingSystem';
import { QuickBooksJobTracking } from 'data/models/QuickBooksJobTracking';

export const enum BillingStatus {
    Exported,
    Approved,
    NonBillable,
    Draft
}

interface BillExportModel {
    Id : () => number;
    Billable : DataSignal<boolean>;
    QBTracking : QuickBooksJobTracking;
    ApprovalOfCharges : () => ApprovalOfCharges | null;
    Bills : () => BillCoverage[];
}

// TH 05/15/2017 -- Additional params that are required to enable the Approve for Export and
// Recalculate actions.
interface BillExportSettingsApproveAndRecalculateParams {
    PersistedModel : BillExportModel;
    BillingSystem : () => BillingSystem | null;
    DraftChargeCollections : DataCacheCollection<DraftChargeCollection, DraftChargeCollectionOptions> | null;
    JobComplete : () => boolean;
    ApproveEndPoint : (key : { BillingSystemId : number, Id : number}) => string;
    RecalculateEndPoint : (key : { Id : number }) => string;
}

export interface BillExportSettingsParams {
    DraftModel : BillExportModel;
    LastExportDate : () => TZDate | null;
    ApproveAndRecalculate : BillExportSettingsApproveAndRecalculateParams | null;
}

export function BillExportSettings(
    ajax : IAjax,
    cache : PCCache,
    {
        DraftModel,
        LastExportDate,
        ApproveAndRecalculate
    } : BillExportSettingsParams
) {
    const approveClient = ajax.Url<{}, ApprovalOfChargesOptions>(() => ApproveAndRecalculate!.ApproveEndPoint({ BillingSystemId: ApproveAndRecalculate!.BillingSystem()!.Id(), Id: DraftModel.Id() })),
        recalculateClient = ajax.Url<{}, DraftChargeCollectionOptions>(() => ApproveAndRecalculate!.RecalculateEndPoint({ Id: DraftModel.Id() })),
        sendingRequest = S.data(false),
        billingStatus = S(() =>
            DraftModel.Bills().length > 0 ? BillingStatus.Exported :
            DraftModel.ApprovalOfCharges() !== null ? BillingStatus.Approved :
            // TH 05/04/2017 -- We don't expect the Approve{Entity}Charges endpoints to check the
            // Billable flag. Essentially, Billable just drives whether the item appears on the
            // Billing Worklist. So we won't require that the Billable status actually be persisted.
            !DraftModel.Billable() ? BillingStatus.NonBillable :
            BillingStatus.Draft
        );

    return {
        DraftModel,
        SendingRequest: sendingRequest,
        LastExportDate,
        ValidForApproval,
        CanRecalculate: () => !!ApproveAndRecalculate,
        Approve,
        Recalculate,
        BillingStatus: billingStatus
    };

    function ValidForApproval() {
        return ApproveAndRecalculate &&
            ApproveAndRecalculate.BillingSystem() &&
            DraftModel.ApprovalOfCharges() === null &&
            DraftModel.Billable() &&
            ApproveAndRecalculate.JobComplete() &&
            // TH 05/04/2017 -- We expect the Approve{Entity}Charges endpoints to require that
            // the entity have a Customer. So it's important we make sure both the saved and
            // draft versions of the Customer are populated before we enable approving charges.
            !!ApproveAndRecalculate.PersistedModel.QBTracking.Customer() &&
            !!DraftModel.QBTracking.Customer();
    }

    async function Approve() {
        if (!ApproveAndRecalculate) throw new Error('Must specify ApproveAndRecalculate params to enable the Approve action.');
        if (sendingRequest()) return;
        sendingRequest(true);
        const req = approveClient.Post({});
        try {
            const charges = await req;
            cache.ApprovalsOfCharges.update1(req.Date, charges);
        } finally {
            sendingRequest(false);
        }
    }

    async function Recalculate() {
        if (!ApproveAndRecalculate) throw new Error('Must specify ApproveAndRecalculate params to enable the Recalculate action.');
        if (sendingRequest()) return;
        sendingRequest(true);
        const approvalsKey = DraftModel.ApprovalOfCharges()!.Id(),
            req = recalculateClient.Post({});
        try {
            const draftCharges = await req;
            S.freeze(() => {
                cache.ApprovalsOfCharges.update(req.Date, [], [approvalsKey]);
                ApproveAndRecalculate.DraftChargeCollections && ApproveAndRecalculate.DraftChargeCollections.update1(req.Date, draftCharges);
            });
        } finally {
            sendingRequest(false);
        }
    }
}
export type BillExportSettings = ReturnType<typeof BillExportSettings>;


// WEBPACK FOOTER //
// ./apps/portcall/billexportsettings/model.ts